import { Box, Flex, HStack, Icon, Text, Tooltip } from "@chakra-ui/react";
import { memo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiChevronRight, FiSend } from "react-icons/fi";
import type { MFCampaignReview } from "state/members/accessReviews";
import { AppLogo, Button } from "components/ds";
import ResourceIcon from "../ResourceIcon";
import { appDisplayName } from "modules/apps/tenants";
import ReviewStatusPill from "./ReviewStatusPill";
import RevisionChip from "./RevisionChip";
import InlineProgress from "./InlineProgress";
import { reviewStatusPillKind, SUBMIT_DECISIONS_LABEL } from "../reviewLogic";
import { resourceRowHref } from "../signOffCampaign";
import StatusBadge from "pages/Members/Attestations/StatusBadge";

/**
 * ResourceCard — a resource row inside the resource drawer (PLO-4803). A flat
 * elevated card (no border): brand logo, resource name, then a meta row of the
 * shared status pill + optional revision chip + an inline progress ring reading
 * "N/M accounts reviewed". A ready-to-submit resource (all accounts reviewed,
 * in-flight, no revisions) gets a green submit affordance before the chevron.
 * Completed resources render dimmed and are non-interactive.
 *
 * An owner sign-off row (PLO-5185) has no accounts: it carries the member's
 * attestation, so it shows the sign-off status badge in place of the account
 * progress and opens the attestation instead of the account-review screen.
 */
export interface ResourceCardProps {
  review: MFCampaignReview;
  campaignId: string;
  /** completed resources are faded and only browsable */
  faded?: boolean;
  /** Admin instruction for this resource (PLO-4817), pre-resolved by the parent. */
  instruction?: string | null;
  /** false for an owner sign-off cycle, whose reviews have no account rows */
  showAccountProgress?: boolean;
}

// Brand logo with an icon fallback — no bordered tile.
const ResourceLogo = ({ review }: { review: MFCampaignReview }) => {
  const { imageUrl, logoUrl, name, type } = review.resource;
  const logo = logoUrl || imageUrl;
  if (logo) return <AppLogo name={name} logoUrl={logo} size="md" />;
  return (
    <Flex
      w="32px"
      h="32px"
      flexShrink={0}
      align="center"
      justify="center"
      borderRadius="component.lg"
      bg="bg.section"
    >
      <ResourceIcon resourceType={type} size={4} />
    </Flex>
  );
};

const ResourceCard = memo(
  ({
    review,
    campaignId,
    faded = false,
    instruction = null,
    showAccountProgress = true,
  }: ResourceCardProps) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const done = review.stats.completed;
    const total = review.stats.total;
    const statusComplete = review.status === "complete";
    const disabled = faded || statusComplete;
    const revision = Boolean(review.hasRevisionRequested);
    // Fully reviewed, in-flight and clean → ready to submit. A
    // sign-off row has no accounts to submit, so it never offers the shortcut.
    const ready =
      showAccountProgress &&
      review.status === "in_progress" &&
      done === total &&
      !revision;
    const attestationStatus = review.attestationStatus ?? null;

    const handleClick = () => {
      if (disabled) return;
      const href = resourceRowHref(review);
      if (review.attestationId) {
        navigate(href);
        return;
      }
      const params = new URLSearchParams(searchParams);
      const queryString = params.toString();
      navigate(`${href}${queryString ? `?${queryString}` : ""}`, {
        state: { campaignId },
      });
    };

    return (
      <Flex
        align="center"
        gap="lg"
        p="1xl"
        bg="bg.container"
        borderRadius="component.2xl"
        boxShadow="elevLow"
        opacity={disabled ? 0.55 : 1}
        cursor={disabled ? "default" : "pointer"}
        transition="box-shadow 0.12s ease-out"
        _hover={disabled ? undefined : { boxShadow: "elevHigh" }}
        onClick={handleClick}
      >
        <ResourceLogo review={review} />

        <Flex direction="column" gap="2xs" flex="1" minW={0}>
          <Text
            fontFamily="body"
            fontWeight={600}
            fontSize="lg"
            color="text.header.default"
            noOfLines={1}
          >
            {appDisplayName(review.resource)}
          </Text>
          <HStack spacing="xs" flexWrap="wrap">
            <ReviewStatusPill kind={reviewStatusPillKind(review.status)} />
            {revision && <RevisionChip />}
            {(attestationStatus || showAccountProgress) && (
              <Text fontSize="sm" color="text.body.tertiary">
                ·
              </Text>
            )}
            {attestationStatus ? (
              <StatusBadge status={attestationStatus} />
            ) : (
              showAccountProgress && (
                <InlineProgress
                  value={done}
                  total={total}
                  complete={statusComplete}
                />
              )
            )}
          </HStack>
          {instruction && (
            <Text
              fontSize="xs"
              color="text.body.tertiary"
              noOfLines={1}
              data-testid="resource-card-instruction"
            >
              {instruction}
            </Text>
          )}
        </Flex>

        {ready && (
          <Tooltip label={SUBMIT_DECISIONS_LABEL} placement="top" hasArrow>
            <Box
              as="span"
              display="inline-flex"
              flexShrink={0}
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
            >
              <Button
                size="md"
                variant="outline"
                tone="success"
                aria-label={SUBMIT_DECISIONS_LABEL}
                icon={<FiSend size={16} />}
              />
            </Box>
          </Tooltip>
        )}

        <Icon
          as={FiChevronRight}
          boxSize="18px"
          color="text.body.tertiary"
          flexShrink={0}
        />
      </Flex>
    );
  },
);

ResourceCard.displayName = "ResourceCard";

export default ResourceCard;
